/**
 * PATH: apps/qa/src/pages/ai-insights/TemplateSuggestionsPanel.tsx
 * PURPOSE: AI-powered template item suggestions panel
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { TemplateSuggestion } from './types'
import { Lightbulb, Plus } from 'lucide-react'

export function TemplateSuggestionsPanel() {
  const [sectionName, setSectionName] = useState('')
  const [suggestions, setSuggestions] = useState<TemplateSuggestion[]>([])

  const handleGetSuggestions = async () => {
    setSuggestions([
      {
        item_text: 'Overlap minimum 100mm verified',
        reasoning: 'AS 4654.2 requires minimum overlap for membrane joints',
        priority: 'required',
        standard_reference: 'AS 4654.2 Clause 4.3',
      },
      {
        item_text: 'Termination bars sealed at perimeter',
        reasoning: 'Common defect point for water ingress',
        priority: 'recommended',
      },
      {
        item_text: 'Bond breaker tape at internal corners',
        reasoning: 'Prevents membrane cracking at stress points',
        priority: 'recommended',
        standard_reference: 'AS 4654.2 Clause 5.2',
      },
    ])
  }

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          Get Template Suggestions
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Enter a section name to get AI-powered checklist item suggestions based on AS/NZS standards.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            placeholder="e.g., Waterproofing, Structural Steel, Electrical..."
            className="flex-1 px-4 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            onClick={handleGetSuggestions}
            disabled={!sectionName}
            className="px-4 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Get Suggestions
          </button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Suggested Items ({suggestions.length})</h3>
            <button className="text-sm text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1">
              <Plus className="w-4 h-4" />
              Add All to Template
            </button>
          </div>
          <div className="divide-y divide-border">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="p-4 hover:bg-accent/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground">{suggestion.item_text}</p>
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        suggestion.priority === 'required' && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                        suggestion.priority === 'recommended' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                        suggestion.priority === 'optional' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      )}>
                        {suggestion.priority}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{suggestion.reasoning}</p>
                    {suggestion.standard_reference && (
                      <p className="text-xs text-violet-600 dark:text-violet-400 mt-1">
                        📚 {suggestion.standard_reference}
                      </p>
                    )}
                  </div>
                  <button className="p-2 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/30 text-violet-600 dark:text-violet-400 transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestions.length === 0 && (
        <div className="bg-card rounded-xl border border-border border-dashed p-8 text-center min-h-[300px] flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Lightbulb className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground">No Suggestions Yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm">
            Enter a section name like "Waterproofing" or "Structural" to get AI-powered checklist item suggestions
          </p>
        </div>
      )}
    </div>
  )
}
